import { Autocomplete, IconButton } from '@mui/joy';
import { forwardRef, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type Control } from './RelationDivisionLine.types';

import CONSTANTS from './RelationDivisionLine.constants';
import { DivisionType } from '~/common/enums';

import { useParent, useUnitRelationOptionsQuery } from '../RelationDivisionLines.hooks';

import { DeleteIcon } from '@/shared/icons/Icons';
import FormField from '@/shared/components/2.0/input/FormField';
import NumberInput from '@/shared/components/2.0/input/NumberInput/NumberInput';

const DISPLAY_NAME = 'RelationDivisionLine';

type UnitRelationAutocompleteProps = {
  control?: Control;
};

const UnitRelationAutocomplete: React.FC<UnitRelationAutocompleteProps> = ({ control }) => {
  const { t } = useTranslation();

  const parent = useParent();
  const query = useUnitRelationOptionsQuery();

  const buildingRelationId = control?.values[CONSTANTS.FORM_KEYS.RELATION];
  const unitId = control?.values[CONSTANTS.FORM_KEYS.UNIT];

  const valueLookup = useMemo(() => {
    if (!buildingRelationId || !unitId) return undefined;

    return { buildingRelationId, unitId };
  }, [buildingRelationId, unitId]);

  const value = useMemo(() => {
    if (!valueLookup) return null;

    return query.selectors.getOption(valueLookup) ?? null;
  }, [query.selectors, valueLookup]);

  const options = useMemo(
    () => query.selectors.getAvailableOptions(valueLookup),
    [query.selectors, valueLookup],
  );

  const error =
    control?.errors[CONSTANTS.FORM_KEYS.RELATION] ?? control?.errors[CONSTANTS.FORM_KEYS.UNIT];

  return (
    <FormField required className="min-w-0 flex-[2]" error={error} label={t('relation')}>
      <Autocomplete
        disabled={parent.readOnly || query.isLoading}
        isOptionEqualToValue={(option, selected) => option.value.key === selected.value.key}
        loading={query.isLoading}
        options={options}
        value={value}
        onChange={(_event, option) => {
          if (!control) return;

          control.handleChange(
            CONSTANTS.FORM_KEYS.RELATION,
            option?.value.buildingRelationId ?? null,
          );
          control.handleChange(CONSTANTS.FORM_KEYS.UNIT, option?.value.unitId ?? null);
        }}
      />
    </FormField>
  );
};

type LineProps = {
  control?: Control;
  divisionType: DivisionType | null;
  maxPercentage: number;
  onFocus?: React.FocusEventHandler<HTMLLIElement>;
  onRemove?: () => void;
  removeDisabled?: boolean;
};

const RelationDivisionLine = forwardRef<HTMLLIElement, LineProps>(
  ({ control, divisionType, maxPercentage, onFocus, onRemove, removeDisabled }, ref) => {
    const { t } = useTranslation();

    const parent = useParent();

    return (
      <li ref={ref} className="mb-4 flex gap-4" onFocus={onFocus}>
        <UnitRelationAutocomplete control={control} />
        <FormField
          required
          className="min-w-0 flex-1"
          error={control?.errors[CONSTANTS.FORM_KEYS.AMOUNT]}
          label={t('amount')}
        >
          <NumberInput
            disabled={parent.readOnly || divisionType !== DivisionType.Amount}
            numericFormat={{
              decimalScale: CONSTANTS.DECIMAL_PRECISION.AMOUNT,
            }}
            size="sm"
            value={control?.values[CONSTANTS.FORM_KEYS.AMOUNT]}
            onValueChange={(values) =>
              control?.handleChange(CONSTANTS.FORM_KEYS.AMOUNT, values.asNumber)
            }
          />
        </FormField>
        <FormField
          required
          className="min-w-0 flex-1"
          error={control?.errors[CONSTANTS.FORM_KEYS.PERCENTAGE]}
          label={t('percentage')}
        >
          <NumberInput
            disabled={parent.readOnly || divisionType !== DivisionType.Percentage}
            numericFormat={{
              decimalScale: CONSTANTS.DECIMAL_PRECISION.PERCENTAGE,
              max: maxPercentage,
              min: 0,
            }}
            size="sm"
            value={control?.values[CONSTANTS.FORM_KEYS.PERCENTAGE]}
            onValueChange={(values) =>
              control?.handleChange(CONSTANTS.FORM_KEYS.PERCENTAGE, values.asNumber)
            }
          />
        </FormField>
        <IconButton
          className="mt-6"
          color="danger"
          disabled={parent.readOnly || removeDisabled}
          variant="plain"
          onClick={onRemove}
        >
          <DeleteIcon />
        </IconButton>
      </li>
    );
  },
);

RelationDivisionLine.displayName = DISPLAY_NAME;

export default memo(RelationDivisionLine);
